import { env } from '@/lib/env';

export type AuthServerErrorCode =
  | 'InvalidCredentials'
  | 'EmailAlreadyRegistered'
  | 'WeakPassword'
  | 'EmailNotVerified'
  | 'InvalidToken'
  | 'TokenExpired'
  | 'InvalidExchangeCode'
  | 'InvalidState'
  | 'OAuthFailed'
  | 'Unauthorized'
  | 'BadRequest'
  | 'NotFound'
  | 'TooManyRequests'
  | 'Internal';

export class AuthServerError extends Error {
  constructor(
    public readonly code: AuthServerErrorCode,
    message?: string,
  ) {
    super(message ?? code);
    this.name = 'AuthServerError';
  }
}

export interface AuthSession {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  user: { id: string; email: string | null; fullName: string | null };
}

export interface SignupVerificationRequired {
  status: 'verification_required';
  user: { id: string; email: string | null; fullName: string } | null;
}

export interface OAuthStartResponse {
  authUrl: string;
  state: string;
}

function baseUrl(): string {
  return env.VITE_AUTH_SERVER_URL.replace(/\/+$/, '');
}

async function request<T>(path: string, init: RequestInit): Promise<T> {
  const res = await fetch(`${baseUrl()}${path}`, {
    headers: { 'Content-Type': 'application/json', ...init.headers },
    ...init,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as {
      error?: AuthServerErrorCode;
      message?: string;
    };
    throw new AuthServerError(body.error ?? 'Internal', body.message);
  }
  return res.json() as Promise<T>;
}

export function postJson<T>(path: string, body: unknown, bearer?: string): Promise<T> {
  const extra: HeadersInit = bearer ? { Authorization: `Bearer ${bearer}` } : {};
  return request<T>(path, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: extra,
  });
}

export function getJson<T>(path: string, bearer?: string): Promise<T> {
  const extra: HeadersInit = bearer ? { Authorization: `Bearer ${bearer}` } : {};
  return request<T>(path, {
    method: 'GET',
    headers: extra,
  });
}
